import { GoogleGenerativeAI } from '@google/generative-ai';

class GeminiService {
  constructor() {
    this.genAI = null;
    this.model = null;
    this.initialized = false;
    this.initError = null;
    this.rateLimitCount = 0;
    this.rateLimitWindow = Date.now();
    this.maxRequestsPerMinute = 10;

    // Initialize if API key is available
    if (process.env.REACT_APP_GEMINI_API_KEY && process.env.REACT_APP_GEMINI_API_KEY !== 'your_gemini_api_key_here') {
      try {
        console.log('Initializing Gemini AI with Gemini 3 Flash Preview...');
        this.genAI = new GoogleGenerativeAI(process.env.REACT_APP_GEMINI_API_KEY);
        this.model = this.genAI.getGenerativeModel({ model: "gemini-3-flash-preview" });
        this.initialized = true;
        console.log('✓ Gemini 3 Flash Preview model initialized successfully');
      } catch (error) {
        console.error('Failed to initialize Gemini AI:', error);
        this.initError = error;
        this.initialized = false;
      }
    } else {
      console.warn('Gemini API key not found or is placeholder');
      this.initError = new Error('API key not configured');
    }
  }

  _checkRateLimit() {
    const now = Date.now();
    const windowDuration = 60000; // 1 minute

    // Reset counter if window has passed
    if (now - this.rateLimitWindow > windowDuration) {
      this.rateLimitCount = 0;
      this.rateLimitWindow = now;
    }

    if (this.rateLimitCount >= this.maxRequestsPerMinute) {
      throw new Error('Rate limit exceeded. Please wait a moment before trying again.');
    }

    this.rateLimitCount++;
  }

  isAvailable() {
    const available = this.initialized && this.model;
    if (!available && this.initError) {
      console.log('Gemini AI not available:', {
        initialized: this.initialized,
        hasModel: !!this.model,
        hasApiKey: !!process.env.REACT_APP_GEMINI_API_KEY,
        initError: this.initError?.message
      });
    }
    return available;
  }

  getInitError() {
    return this.initError;
  }

  async _makeRequest(prompt) {
    try {
      this._checkRateLimit();
      
      const result = await this.model.generateContent(prompt);
      const response = result.response;
      return response.text();
    } catch (error) {
      if (error.message?.includes('rate limit') || error.message?.includes('quota')) {
        throw new Error('API quota exceeded. Please try again later.');
      } else if (error.message?.includes('API_KEY_INVALID')) {
        throw new Error('Invalid API key. Please check your configuration.');
      } else if (error.message?.includes('billing')) {
        throw new Error('Billing must be enabled for this API key.');
      } else {
        throw new Error(`AI service error: ${error.message}`);
      }
    }
  }

  async improveDescription(title, description, category) {
    if (!this.isAvailable()) {
      return {
        success: false,
        message: this.initError?.message || 'AI service not available',
        suggestions: []
      };
    }

    try {
      const prompt = `As a crowdfunding expert, help improve this campaign description:
        
Title: "${title}"
Category: "${category}"
Current Description: "${description}"

Please provide:
1. A quality score (1-10) with brief explanation
2. 2-3 specific improvement suggestions
3. An improved version of the description (keep it concise, max 200 words)

Format your response as JSON:
{
  "qualityScore": number,
  "scoreExplanation": "brief explanation",
  "suggestions": ["suggestion 1", "suggestion 2"],
  "improvedDescription": "improved description"
}`;

      const text = await this._makeRequest(prompt);
      
      try {
        const jsonMatch = text.match(/\{[\s\S]*\}/);
        if (jsonMatch) {
          const parsed = JSON.parse(jsonMatch[0]);
          return {
            success: true,
            ...parsed
          };
        }
      } catch (parseError) {
        console.error('Failed to parse AI response:', parseError);
      }
      
      return {
        success: true,
        qualityScore: 7,
        scoreExplanation: "AI analysis completed",
        suggestions: [
          "Consider adding more specific details about fund usage",
          "Include a clear timeline for your project",
          "Add information about your background or credentials"
        ],
        improvedDescription: description
      };
      
    } catch (error) {
      console.error('Gemini AI error:', error);
      return {
        success: false,
        message: error.message,
        suggestions: []
      };
    }
  }

  async suggestCategory(title, description) {
    if (!this.isAvailable()) {
      return {
        success: false,
        suggestedCategory: 'Other'
      };
    }

    try {
      const categories = ['Charity', 'Education', 'Medical', 'Technology', 'Environment', 'Arts', 'Community', 'Other'];
      
      const prompt = `Based on this campaign information, suggest the most appropriate category:
        
Title: "${title}"
Description: "${description}"

Available categories: ${categories.join(', ')}

Respond with just the category name that best fits.`;

      const text = await this._makeRequest(prompt);
      const suggestedCategory = text.trim();
      
      const validCategory = categories.find(cat => 
        suggestedCategory.toLowerCase().includes(cat.toLowerCase())
      );
      
      return {
        success: true,
        suggestedCategory: validCategory || 'Other',
        confidence: validCategory ? 'high' : 'low'
      };
      
    } catch (error) {
      console.error('Category suggestion error:', error);
      return {
        success: false,
        suggestedCategory: 'Other'
      };
    }
  }

  async testConnection() {
    if (!this.isAvailable()) {
      return { success: false, message: 'Service not initialized' };
    }

    try {
      const text = await this._makeRequest('Say "Hello" in one word.');
      return { success: true, message: 'Connection successful', response: text };
    } catch (error) {
      console.error('Gemini API test failed:', error);
      return { success: false, message: error.message };
    }
  }
}

const geminiService = new GeminiService();
export default geminiService;